import { withNamespaces } from "react-i18next";
import { useRef, useState } from "react";
import {
	Col,
	Row,
	Button,
	Spinner,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from "reactstrap";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
	errorNotification,
	successNotification,
} from "src/store/actions";
import QuestionarService from "src/modules/data-protection/components/Questionar/service";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import ProcessingActivityService from "src/modules/data-protection/apis/ProcessingActivityService";
import Accordion from "react-bootstrap/Accordion";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import {
    helpers
} from '@smartintegrity/questionnaire';
import OverviewHeader from "../../../overview-header";
import CreationModal from "./components/CreationModal";
import EditModal from "./components/EditModal";

import "./style.scss";
import ProcessingQuestionnaire from "./components/ProcessingQuestionnaire";

import { 
    mapQuestions, 
    mapAnswers
} from "src/modules/data-protection/helpers/questionnaire";

const ProcessingActivity = ({
	t,
	organization
}) => {
	const { softwareId } = useParams();
	const [ activityToDelete, setActivityToDelete ] = useState();
    const [ activityToEdit, setActivityToEdit ] = useState();
	const [ showCreationModal, setShowCreationModal ] = useState(false);

	const pageEndRef = useRef();

	const toggleActivityDeletionModal = () => setActivityToDelete(!activityToDelete);
	const toggleShowCreationModal = () => setShowCreationModal(!showCreationModal);
    const toggleActivityEditModal = () => setActivityToEdit(null);

    const processingQuestionsQuery = useQuery({
		queryKey: [
			"data-protection-softwares-questions-list",
			softwareId
		],
		queryFn: async () => {
			const service = QuestionarService.getInstance();

			return await service.questions(
				"processing_activity",
				organization.id,
				{
					softwareId: softwareId
				}
			);
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: () => {
			if (process.env.NODE_ENV === 'development');

			toast(t("An error occurred while fetching questions."), {
				type: "error"
			});
		},
	});
    
	const {
		data: filledQuestionnaires,
		refetch: refetchActivities,
        isFetching: filledQuestionnairesAreFetching
	} = useQuery({
		queryKey: [
			"data-protection-fetch-software-processing-activities",
			softwareId,
            processingQuestionsQuery.dataUpdatedAt
		],
		queryFn: async () => {
			const service = DataProtectionSoftwareService.getInstance();

			const response =  await service.filledQuestionnaires(
				softwareId,
				"processing_activity"
			);

            return (response.reports || []).map((report) => {
                const mappedAnswers = mapAnswers(report.question_answers || []);

                const questions = mapQuestions(processingQuestionsQuery.data || [], mappedAnswers);

                return {
                    ...report,
                    questions: helpers.makeQuestionsReadyForQuestionnaire(
                        questions,
                        false
                    ),
                    question_answers: mappedAnswers,
                    mappedAnswers: helpers.makePreviousAnswersReadyForQuestionnaire(
                        questions,
                        mappedAnswers
                    )
                }
            })
		},
        enabled: processingQuestionsQuery.isFetched,
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: (error) => {
			if (process.env.NODE_ENV === 'development') console.error(error);

			toast(t("An error occurred while fetching processing activities."), {
				type: "error"
			});
		}
	});

	const {
		data: softwareDetails,
		isFetching: softwareDetailIsFetching
	} = useQuery({
		queryKey: ["data-protection-software-details", softwareId],
		queryFn: async () => {
			const service = DataProtectionSoftwareService.getInstance();
			return await service.details(softwareId);
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: (error) => {
			if (process.env.NODE_ENV === 'development') console.error(error);
			toast(t("An error occurred while fetching software details."), {
				type: "error"
			});
		},
	});

	const {
		mutate: deleteActivity,
		isLoading: activityDeletionIsLoading
	} = useMutation({
		mutationFn: async ({
			caseId
		}) => {
			const service = ProcessingActivityService.getInstance();
			return await service.deleteActivity({
				report_case_id: caseId
			})
		},
		onSuccess() {
			toast(t("Activity is removed"), {
				type: 'success'
			});
			refetchActivities();
			setActivityToDelete(null);
		},
		onError(error) {
			if (process.env.NODE_ENV === 'development') console.error(error)
			toast(t('Faild to remove the activity'), {
				type: "error"
			})
		}
	});

	return (
		<>
			<Row>
				<Col sm="12">
					<div className="p-4">
						<OverviewHeader
							assetId={softwareId}
						/>
						<div className="d-flex justify-content-end mt-4">
							<Button
								color="primary"
								outline
								onClick={() => setShowCreationModal(true)}
								disabled={softwareDetailIsFetching || filledQuestionnairesAreFetching}
							>
								{t("Add New Activity")}
							</Button>
						</div>

                        {filledQuestionnairesAreFetching || processingQuestionsQuery.isFetching ? (
                            <div className="mt-4">
                                <div className="w-100">
                                    <p 
                                        style={{height: '55px'}} 
                                        className='w-100 dt-field dt-skeleton' 
                                    />
                                </div>
                                <div className="w-100">
                                    <p
                                        style={{height: '55px'}} 
                                        className='w-100 dt-field dt-skeleton' 
                                    />
                                </div>
                                <div className="w-100">
                                    <p 
                                        style={{height: '55px'}} 
                                        className='w-100 dt-field dt-skeleton' 
                                    />
                                </div>
                            </div>
                        ) : (
                            <Accordion
                                className="mt-4 smartintegrity__questionnaire__tabs_progress"
                            >
                                {(filledQuestionnaires || []).map((report, index) => {
                                    return (
                                        <ProcessingQuestionnaire 
                                            key={index}
                                            report={report}
                                            index={index}
                                            setActivityToDelete={setActivityToDelete}
                                            setActivityToEdit={setActivityToEdit}
                                            softwareId={softwareId}
                                        />
                                    )
                                })}
                            </Accordion>
                        )}
					</div>
				</Col>
			</Row>

			<span ref={pageEndRef} />

			<Modal
				isOpen={activityToDelete}
				toggle={toggleActivityDeletionModal}
			>
				<ModalHeader
					toggle={toggleActivityDeletionModal}
				>
					{t("Delete Activity")}
				</ModalHeader>
				<ModalBody>
					<p>
						{t("Are you sure?")}
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						color="secondary"
						onClick={toggleActivityDeletionModal}
						className="me-2"
					>
						{t("Cancel")}
					</Button>

					<Button
						color="danger"
						onClick={() => deleteActivity({
							caseId: activityToDelete
						})}
						disabled={activityDeletionIsLoading}
					>
						{activityDeletionIsLoading && (
							<Spinner size='sm' />
						)}
						{t("Delete")}
					</Button>
				</ModalFooter>
			</Modal>

			{softwareDetails && (
                <>
                    <CreationModal 
                        toggleModal={toggleShowCreationModal}
                        modalIsOpen={showCreationModal}
                        onCreationSuccess={() => {
							refetchActivities();

							setTimeout(() => {
								if (pageEndRef.current) {
									pageEndRef.current.scrollIntoView({
										behavior: 'smooth',
										block: 'center',
										inline: 'nearest'
									})
								}
							}, 500);
						}}
                        software={softwareDetails}
                    />
                    <EditModal 
                        toggleModal={toggleActivityEditModal}
                        modalIsOpen={!!activityToEdit}
                        oldData={activityToEdit}
                        onEditSuccess={() => refetchActivities()}
                        software={softwareDetails}
                    />
                </>
			)}
		</>
	);
};

const mapStatetoProps = (state) => {
	const { token, user } = state.Login;
	const { Organization } = state;

	return {
		token,
		user,
		organization: Organization,
	};
};

export default withNamespaces()(
	connect(mapStatetoProps, {
		errorNotification,
		successNotification,
	})(ProcessingActivity)
);
